import { VStack, Flex, HStack, Button, Text, Box } from '@cad-arc';
import { format } from 'date-fns';
import { ActionTypeEnum, DexActivityTabEntry, UpdateToEventView } from '@mark43/cad-api';
import { ResultImage } from '../../../data-exchange/results/result-image';
import { useEnabledFeatures } from '../../../common/hooks/enabled-features/use-enabled-features';
import { DexUpdateToEvent } from '../../../data-exchange/update-to-event/dex-update-to-event';
import { EventAutocompleteComponent } from '../../../data-exchange/components/event-autocomplete';
import { AppNameProvider, AppName } from '@m43/core';
import { useApplicationType } from '../../../common/hooks/use-application-type';
import { useUnitAssignment } from '../../../unit-assignment/unit-assignment-context';
import { colors } from '../../../colors/colors';

interface DexActivityEntryProps {
    entry: DexActivityTabEntry;
    updateToEventView?: UpdateToEventView;
}

export const DexActivityEntry: React.FC<DexActivityEntryProps> = ({ entry }) => {
    const enabledFeatures = useEnabledFeatures();
    const { dexActivityTabUpdateToEventView, elasticUserAction } = entry;
    const { isFirstResponder } = useApplicationType();
    const unitAssignment = useUnitAssignment();
    const unitAssignedEventId = unitAssignment?.assignedEvent?.eventId;

    const dexMessageId = dexActivityTabUpdateToEventView?.incomingMessageId;

    const resultMessage =
        enabledFeatures.useDexResultMessageProperty && elasticUserAction.dexResultMessage
            ? elasticUserAction.dexResultMessage
            : elasticUserAction.rawDexResultMessage;

    return (
        <VStack
            key={elasticUserAction.id}
            align="left"
            w="100%"
            {...(isFirstResponder
                ? {
                      backgroundColor: 'var(--arc-colors-surface-accent)',
                      padding: '12px',
                      borderRadius: '3px',
                  }
                : {
                      paddingBottom: enabledFeatures.rollOutCadEventPanelDexResultsUseVirtuoso
                          ? '24px'
                          : '12px',
                  })}
        >
            <Flex flexDirection="row" justifyContent="space-between">
                <Text fontWeight="semibold">{elasticUserAction.eventLogMessage}</Text>
                <Box w="275px" marginLeft="4px" textAlign="right">
                    <Text fontFamily="mono" variant="caption" color="secondary">
                        {format(new Date(elasticUserAction.createdDateUtc), 'MM/dd/yy HH:mm:ss')}
                    </Text>
                </Box>
            </Flex>
            {dexActivityTabUpdateToEventView?.showUpdateToEvent ? (
                <Box align="end" mt="0px !important">
                    <AppNameProvider appName={AppName.CAD_NEXT}>
                        <DexUpdateToEvent
                            key={dexMessageId}
                            dexMessageId={dexMessageId}
                            disableInitialBootstrap
                            unitAssignedEventId={isFirstResponder ? unitAssignedEventId || null : undefined}
                            EventAutocompleteComponent={isFirstResponder ? undefined : EventAutocompleteComponent}
                            showTrailingVisualIcon
                        />
                    </AppNameProvider>
                </Box>
            ) : undefined}
            {enabledFeatures.imagesInDexResults && elasticUserAction.resourcePaths[0] ? (
                <HStack justifyContent="space-between" alignItems="start">
                    <ResultImage mark43FilePath={elasticUserAction.resourcePaths[0]} />
                    <Button
                        as="a"
                        href={elasticUserAction.resourcePaths[0]}
                        download
                        trailingVisual="Download"
                        variant="ghost"
                    >
                        Download Photo
                    </Button>
                </HStack>
            ) : undefined}
            {!!resultMessage && (
                <Box
                    backgroundColor={
                        elasticUserAction.actionType ===
                        ActionTypeEnum.DEX_HIT_RETURNED_FOR_EVENT.name
                            ? colors.dexActivityHitBackground
                            : 'surface.foreground'
                    }
                    borderRadius="var(--arc-radii-md)"
                    w="100%"
                    padding="10px"
                    whiteSpace="pre-line"
                >
                    <Text fontFamily="mono" variant="caption" style={{
                        color: elasticUserAction.actionType ===
                        ActionTypeEnum.DEX_HIT_RETURNED_FOR_EVENT.name
                            ? colors.dexActivityHitText
                            : 'text.primary'
                    }}>
                        {resultMessage}
                    </Text>
                </Box>
            )}
        </VStack>
    );
};
